<template>
    <div class="sms-broadcast-text-box">
        <div
            :class="['text-box-container', { error: hasError || isMessageTooLong, 'hide-border': hideBorder }]"
            @click="handleFocus"
        >
            <div
                v-if="isBrowserSupported"
                ref="content"
                class="text-box"
                :class="{ placeholder: !value }"
                contenteditable="true"
                :placeholder="textAreaPlaceholder"
                @beforeinput="handleBeforeInput"
                @paste="handlePaste"
                @keydown="handleKeydown"
            >
                <div v-for="(row, i) in content.rows" :key="i" data-row>
                    <span
                        v-for="({ type, text, label, component, isOverflow }, j) in row.values"
                        :key="j"
                        data-value
                    >
                        <span v-if="type === 'text'" class="text" :class="{ overflow: isOverflow }">
                            <template v-if="text || row.values.length > 1">{{ text }}</template>

                            <br v-else />
                        </span>

                        <span v-else-if="type === 'component' && component === 'chip'" :class="{ overflow: isOverflow }" data-merge-field>
                            <SmsBroadcastMergeFieldChip
                                :label="label"
                                :is-overflow="isOverflow"
                            />
                        </span>
                    </span>
                </div>
            </div>

            <textarea
                v-else
                ref="message"
                v-model="internalValue"
                class="message"
                placeholder="Enter message"
                @blur="handleBlur"
                @focus="handleFocus"
                @input="handleInput"
            />
            <slot name="footer" />
        </div>

        <slot
            name="secondaryFooter"
        />
    </div>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash.debounce';
import SmsBroadcastMergeFieldChip from './SmsMergeFieldChip';
import { textToRogue } from './rogue.util';
import {
    MAX_TEXT_LIMIT_TEXT_BOX,
    SINGLE_MESSAGE_COUNT,
} from './sms.constants';
import { TEMPLATE_KINDS } from '../shared/template.constants.js';
import { containsSupportedCharacters } from './automatedSms.util';
import { hasUnsupportedMergeField } from '@/components/broadcast/utils/smsBroadcast.util';
import { MAX_CHARACTER_COUNT, SMS_MESSAGE_LENGTH } from '@/components/broadcast/constants/smsBroadcast.constants';
import { getMergeFieldsForCB } from '@/components/funnel/konva-campaign-editor/configuration/task.flow.api';

const getIndex = ({ content, position }) => {
    let index = 0;
    const { rowIndex, valueIndex, cursorIndex } = position;

    content.rows.forEach((row, i) => {
        row.values.forEach(({ text }, j) => {
            if (i < rowIndex || i === rowIndex && j < valueIndex) {
                index += text.length;
            } else if (i === rowIndex && j === valueIndex) {
                index += cursorIndex;
            }
        });

        // Handle new line character
        if (rowIndex > i) {
            index += 1;
        }
    });

    return index;
};

const getNodeIndices = ({ content, node }) => {
    let row = node.parentNode.closest('[data-row]');

    if (!row && node.getAttributeNames().includes('data-row')) row = node;
    const rowIndex = [...content.children].indexOf(row);
    const value = node.parentNode.closest('[data-value]');
    const valueIndex = [...row.children].indexOf(value);

    return {
        rowIndex,
        valueIndex,
    };
};

export default {
    components: {
        SmsBroadcastMergeFieldChip,
    },

    provide() {
        return {
            $richTextBox: {
                insertMergeField: this.handleMergeFieldInsert,
                insertValue: this.handleInsertValue,
                insertTemplate: this.insertTemplate,
                errorMessage: () => this.errorMessage,
                hasError: () => this.hasError,
                isMessageTooLong: () => this.isMessageTooLong,
                messageHasMergeField: () => this.messageHasMergeField,
                characterCount: () => this.characterCount,
                textMessagesCount: () => this.textMessagesCount,
                hasUnsupportedMergeField: () => this.hasUnsupportedMergeField,
                hasUnsupportedCharacters: () => this.hasUnsupportedCharacters,
            },
        };
    },

    props: {
        hasError: Boolean,
        value: {
            type: String,
            default: '',
        },
        undoHistoryDelay: {
            type: Number,
            default: 500,
        },
        hideBorder: Boolean,
        isSmsTextArea: {
            type: Boolean,
            default: true,
        },
        defaultPlaceHolder: {
            type: String,
            default: null,
        },
        hasLegacyFormat: {
            type: Boolean,
            default: false,
        },
        funnelId: {
            type: Number,
            default: 0,
        },
    },

    data() {
        return {
            isSpaApp: false,
            internalValue: '',
            mergeFieldCategories: [],
            selection: { length: 0, index: 0 },
            undoHistory: {
                past: [],
                future: [],
            },
            isMergeFieldPopoverOpen: false,
            popoverOpen: false,
            templateType: TEMPLATE_KINDS.KMN_AUTOMATION_CUSTOM,
            ftu: null,
        };
    },

    mounted() {
        document.addEventListener('selectionchange', this.handleSelectionChange);

        if (this.undoHistoryDelay) {
            this.addToHistory = debounce(this.addToHistory, this.undoHistoryDelay, { trailing: false, leading: true });
        }

        this.loadMergeFields();
    },

    beforeDestroy() {
        document.removeEventListener('selectionchange', this.handleSelectionChange);
    },

    watch: {
        value: {
            handler(value, oldValue) {
                if (value !== oldValue) {
                    this.internalValue = value || '';
                }
            },
            immediate: true,
        },
    },

    computed: {
        characterCount() {
            return this.internalValue.length;
        },

        content() {
            return textToRogue({
                text: this.internalValue,
                maxCharacterCount: this.maxCharacterCount,
                ...(this.mergeFields && { mergeFields: this.mergeFields }),
            });
        },

        errorMessage() {
            if (this.hasError && !this.isMessageTooLong && containsSupportedCharacters(this.internalValue)) {
                return this.$t('sms.broadcast.richTextBox.error.messageEmpty');
            }

            if (this.hasError && !containsSupportedCharacters(this.internalValue)) {
                return this.$t('sms.broadcast.richTextBox.error.unsupportedCharacters');
            }

            if (this.isMessageTooLong) {
                return this.$tc('sms.broadcast.richTextBox.error.messageTooLong', this.maxCharacterCount, { count: this.maxCharacterCount });
            }

            return '';
        },

        textMessagesCount() {
            if (this.characterCount > MAX_CHARACTER_COUNT) {
                return Math.ceil(this.characterCount / SINGLE_MESSAGE_COUNT);
            }

            return Math.ceil(this.characterCount / MAX_CHARACTER_COUNT);
        },

        isMessageTooLong() {
            return this.characterCount > this.maxCharacterCount;
        },

        isBrowserSupported() {
            return Boolean(window.chrome);
        },

        mergeFields() {
            return this.mergeFieldCategories.reduce((mergeFields, category) => {
                category.fields.forEach((field) => {
                    mergeFields[field.value] = field.label;
                });

                return mergeFields;
            }, {});
        },

        messageHasMergeField() {
            return this.content.rows.some(({ values }) => values.some(({ type, component }) => type === 'component' && component === 'chip'));
        },

        hasUnsupportedMergeField() {
            const mergeFieldValues = Object.keys(this.mergeFields);

            return hasUnsupportedMergeField(this.value, mergeFieldValues);
        },

        hasUnsupportedCharacters() {
            return !containsSupportedCharacters(this.internalValue);
        },

        maxCharacterCount() {
            return SMS_MESSAGE_LENGTH.MAXIMUM;
        },
        textAreaPlaceholder() {
            if (this.defaultPlaceHolder) {
                return this.defaultPlaceHolder;
            }

            return this.isSmsTextArea ? this.$t('sms.broadcast.richTextBox.messagePlaceholder') : this.$t('funnel.edit.sideBar.goal.apply.note.placeholder.description');
        },
    },

    methods: {
        handleBlur() {
            this.$emit('blur');
        },

        handleFocus() {
            this.$emit('focus');
        },

        async loadMergeFields() {
            try {
                if (this.hasLegacyFormat) {
                    this.mergeFieldCategories = await getMergeFieldsForCB(this.funnelId);
                } else {
                    const params = new URLSearchParams({ usage: 'sms' });

                    ['contact', 'contactCustomFields', 'profile', 'today', 'user'].forEach((category) => {
                        params.append('categories', category);
                    });

                    const { data } = await axios.get(`/app/merge/fields?${params.toString()}`);

                    this.mergeFieldCategories = data.categories;
                }
            } catch (e) {
                this.$toast({ message: 'There was an error loading the available merge fields. Please try again.' });
            }
        },

        async setCursor() {
            await this.$nextTick();

            const { index } = this.selection;
            let rowIndex = -1;
            let valueIndex = -1;
            let cursorIndex = -1;
            let length = 0;

            this.content.rows.forEach((row, i) => {
                row.values.forEach((value, j) => {
                    length += value.text.length;

                    if (length >= index && rowIndex < 0 && valueIndex < 0) {
                        rowIndex = i;
                        valueIndex = j;
                        cursorIndex = index - (length - value.text.length);
                    }
                });

                if (i !== this.content.rows.length - 1) {
                    length += 1;
                }
            });

            const row = this.$refs.content.children[rowIndex];
            const values = row.querySelectorAll('span[data-value] span');
            let value = values[valueIndex];

            if (value && value.matches('[data-merge-field]')) {
                if (cursorIndex === 0) {
                    value = values[valueIndex - 1];
                } else {
                    value = values[valueIndex + 1];
                    cursorIndex = 0;
                }
            }

            const selection = document.getSelection();

            selection.removeAllRanges();
            const range = document.createRange();

            range.setStart(value.firstChild, cursorIndex);
            selection.addRange(range);
        },

        getMergeFieldPosition(index) {
            const mergeFieldValues = Object.keys(this.mergeFields);

            let hasMergeField = false;
            let mergeStartIndex = 0;
            let mergeEndIndex = 0;

            mergeFieldValues.forEach((mergeField) => {
                let offset = 0;
                let noMoreMatches = false;

                while (!noMoreMatches) {
                    const match = this.internalValue.indexOf(mergeField, offset);

                    if (match === -1) {
                        noMoreMatches = true;
                    } else {
                        offset += mergeField.length;

                        if (match < index && index < match + mergeField.length) {
                            hasMergeField = true;
                            mergeStartIndex = match;
                            mergeEndIndex = match + mergeField.length;
                        }
                    }
                }
            });

            return {
                mergeStartIndex,
                mergeEndIndex,
                hasMergeField,
            };
        },

        handleBeforeInput(event) {
            event.preventDefault();
            this.handleInputEvent(event);
            this.update();
        },

        handleInputEvent(event) {
            this.addToHistory({ selection: { ...this.selection }, text: this.internalValue });
            const { data, inputType } = event;

            switch (inputType) {
            case 'insertText':
                this.insertText(data);
                break;
            case 'deleteContentBackward':
                this.deleteContentBackward();
                break;
            case 'deleteByCut':
            case 'deleteContentForward':
                this.deleteContentForward();
                break;
            case 'insertParagraph':
            case 'insertLineBreak':
                this.insertNewLine();
                break;
            default:
                break;
            }

            this.setCursor();
        },

        handleInsertValue(value) {
            this.insertText(value);
            this.update();
        },

        insertText(data) {
            const { length, index } = this.selection;
            let beforeIndex = index;
            let afterIndex = index + length;
            let newIndex = index + data.length;

            if (data.length > MAX_TEXT_LIMIT_TEXT_BOX || (this.internalValue.length + data.length) > MAX_TEXT_LIMIT_TEXT_BOX) {
                this.$error({
                    message: 'Your text exceeds the character limit of 10,000 and you cannot add more to this field. Please shorten it.',
                    bottom: true,
                });

                return;
            }

            const { hasMergeField: beforeHasMergeField, mergeStartIndex: beforeMergeStartIndex, mergeEndIndex: beforeMergeEndIndex } = this.getMergeFieldPosition(beforeIndex);

            if (beforeHasMergeField) {
                if (length > 0) {
                    beforeIndex = beforeMergeStartIndex;
                    afterIndex += index - beforeMergeStartIndex;
                    newIndex = beforeMergeStartIndex + data.length;
                } else {
                    beforeIndex = beforeMergeEndIndex;
                    afterIndex = beforeMergeEndIndex;
                    newIndex = beforeMergeEndIndex + data.length;
                }
            }

            const { hasMergeField: afterHasMergeField, mergeEndIndex: afterMergeEndIndex } = this.getMergeFieldPosition(afterIndex);

            if (afterHasMergeField && length > 0) {
                afterIndex = afterMergeEndIndex;
            }

            const before = this.internalValue.slice(0, beforeIndex);
            const after = this.internalValue.slice(afterIndex);

            this.internalValue = `${before}${data}${after}`;
            this.selection.index = newIndex;
            this.selection.length = 0;
        },

        deleteContentBackward() {
            const { index, length } = this.selection;
            const text = this.internalValue;

            // Check if cursor is not at the beginning
            if (index !== 0 || length !== 0) {
                let before;
                let after;

                // Handle delete backward with no selection range
                if (length === 0) {
                    const mergeFieldValues = Object.keys(this.mergeFields);
                    let mergeFieldLength = 0;

                    const isSelectionAtEndOfMergeField = mergeFieldValues.some((mergeField) => {
                        const isIndexAtEndOfMergeField = text.slice(index - mergeField.length, index) === mergeField;

                        if (isIndexAtEndOfMergeField) {
                            mergeFieldLength = mergeField.length;
                        }

                        return isIndexAtEndOfMergeField;
                    });

                    const { hasMergeField: isSelectionInMergeField, mergeStartIndex, mergeEndIndex } = this.getMergeFieldPosition(index);

                    if (isSelectionAtEndOfMergeField) {
                        before = text.slice(0, index - mergeFieldLength);
                        after = text.slice(index);

                        this.selection.index -= mergeFieldLength;
                    } else if (isSelectionInMergeField) {
                        before = text.slice(0, mergeStartIndex);
                        after = text.slice(mergeEndIndex);

                        this.selection.index = mergeStartIndex;
                    } else {
                        before = text.slice(0, index - 1);
                        after = text.slice(index);

                        this.selection.index -= 1;
                    }

                    this.internalValue = `${before}${after}`;

                    // Handle delete backward with selection range
                } else {
                    this.deleteSelection();
                }
            }
        },

        deleteContentForward() {
            const { index, length } = this.selection;
            const text = this.internalValue;

            // Check if cursor is not at the end
            if (index !== text.length) {
                let before;
                let after;

                // Handle delete forward with no selection range
                if (length === 0) {
                    const mergeFieldValues = Object.keys(this.mergeFields);
                    let mergeFieldLength = 0;

                    const isSelectionAtStartOfMergeField = mergeFieldValues.some((mergeField) => {
                        const isIndexAtStartOfMergeField = text.slice(index, index + mergeField.length) === mergeField;

                        if (isIndexAtStartOfMergeField) {
                            mergeFieldLength = mergeField.length;
                        }

                        return isIndexAtStartOfMergeField;
                    });

                    const { hasMergeField: isSelectionInMergeField, mergeStartIndex, mergeEndIndex } = this.getMergeFieldPosition(index);

                    if (isSelectionAtStartOfMergeField) {
                        before = text.slice(0, index);
                        after = text.slice(index + mergeFieldLength);
                    } else if (isSelectionInMergeField) {
                        before = text.slice(0, mergeStartIndex);
                        after = text.slice(mergeEndIndex);

                        this.selection.index = mergeStartIndex;
                    } else {
                        before = text.slice(0, index);
                        after = text.slice(index + 1);
                    }

                    this.internalValue = `${before}${after}`;

                    // Handle delete forward with selection range
                } else {
                    this.deleteSelection();
                }
            }
        },

        deleteSelection() {
            const { length, index } = this.selection;
            let beforeIndex = index;
            let afterIndex = index + length;

            const { hasMergeField: beforeHasMergeField, mergeStartIndex: beforeMergeStartIndex } = this.getMergeFieldPosition(beforeIndex);

            if (beforeHasMergeField) {
                afterIndex += index - beforeMergeStartIndex;
                beforeIndex = beforeMergeStartIndex;
                this.selection.index = beforeMergeStartIndex;
            }

            const { hasMergeField: afterHasMergeField, mergeEndIndex: afterMergeEndIndex } = this.getMergeFieldPosition(afterIndex);

            if (afterHasMergeField) {
                afterIndex = afterMergeEndIndex;
            }

            const before = this.internalValue.slice(0, beforeIndex);
            const after = this.internalValue.slice(afterIndex);

            this.selection.length = 0;
            this.internalValue = `${before}${after}`;
        },

        insertNewLine() {
            const before = this.internalValue.slice(0, this.selection.index);
            const after = this.internalValue.slice(this.selection.index + this.selection.length);

            this.internalValue = `${before}\n${after}`;
            this.selection.index += 1;
            this.selection.length = 0;
        },

        handlePaste(event) {
            event.preventDefault();

            const pastedPlainText = event.clipboardData.getData('text/plain');

            this.addToHistory({ selection: { ...this.selection }, text: this.internalValue });
            this.insertText(pastedPlainText);
            this.setCursor();

            this.update();
        },

        handleSelectionChange() {
            const selection = document.getSelection();
            const { content } = this.$refs;

            if (content?.contains(selection.anchorNode)) {
                const range = selection.getRangeAt(0);
                let start;
                let end;

                if (!range.collapsed) {
                    start = getNodeIndices({ content, node: range.startContainer });
                    start.cursorIndex = range.startOffset;

                    end = getNodeIndices({ content, node: range.endContainer });
                    end.cursorIndex = range.endOffset;
                } else {
                    start = getNodeIndices({ content, node: selection.anchorNode });
                    start.cursorIndex = selection.anchorOffset;

                    end = start;
                }

                if (start) {
                    const index = getIndex({ content: this.content, position: start });
                    const length = getIndex({ content: this.content, position: end }) - index;

                    this.selection = { length, index };
                }
            }
        },

        handleMergeFieldInsert(value) {
            if (!this.isBrowserSupported) {
                const { message } = this.$refs;
                const { selectionStart, selectionEnd } = message;
                const before = this.internalValue.substring(0, selectionStart);
                const after = this.internalValue.substring(selectionEnd, this.internalValue.length);

                this.internalValue = `${before}${value}${after}`;

                // Set cursor to end of merge field
                message.selectionStart = selectionStart + value.length;
                message.selectionEnd = selectionStart + value.length;

                message.focus();
            } else {
                this.addToHistory({ selection: { ...this.selection }, text: this.internalValue });
                this.insertText(value);
                this.setCursor();
            }
            this.update();
        },

        // Input for textarea only
        handleInput(e) {
            const { value } = e.target;
            const element = this.$refs.message;

            if (element) {
                element.style.height = 'auto';
                element.style.height = `${element.scrollHeight}px`;
            }

            this.internalValue = value;
            this.update();
        },

        update() {
            this.$emit('input', this.internalValue);
            this.$emit('update:value', this.internalValue);
        },

        handleKeydown(event) {
            const {
                key, metaKey, ctrlKey, shiftKey,
            } = event;

            if (key === 'z' && (metaKey ^ ctrlKey) && !shiftKey) {
                event.preventDefault();
                this.handleUndo();
            }

            if (key.toLowerCase() === 'z' && (metaKey ^ ctrlKey) && shiftKey) {
                event.preventDefault();
                this.handleRedo();
            }

            if (key === 'y' && (metaKey ^ ctrlKey) && !shiftKey) {
                event.preventDefault();
                this.handleRedo();
            }
        },

        addToHistory({ selection, text }) {
            this.undoHistory.past.push({ selection, text });
            this.undoHistory.future = [];
        },

        handleUndo() {
            const { past, future } = this.undoHistory;

            const present = {
                selection: this.selection,
                text: this.internalValue,
            };

            if (past.length > 0) {
                const previous = past.pop();

                this.undoHistory = {
                    past,
                    future: [present, ...future],
                };

                this.setInternalValueAndSelection({
                    internalValue: previous.text,
                    selection: previous.selection,
                });
            }
        },

        handleRedo() {
            const { past, future } = this.undoHistory;

            const present = {
                selection: this.selection,
                text: this.internalValue,
            };

            if (future.length > 0) {
                const next = future.shift();

                this.undoHistory = {
                    past: [...past, present],
                    future,
                };

                this.setInternalValueAndSelection({
                    internalValue: next.text,
                    selection: next.selection,
                });
            }
        },

        setInternalValueAndSelection({ internalValue, selection = { index: 0, length: 0 } }) {
            this.internalValue = internalValue;
            this.selection = selection;
            this.update();
            this.setCursor();
        },

        toggleMergeFieldPopover() {
            if (!this.messageHasMergeField) {
                return;
            }

            this.isMergeFieldPopoverOpen = !this.isMergeFieldPopoverOpen;
        },

        insertTemplate(template) {
            this.internalValue = template.content;

            this.update();
        },
    },
};
</script>

<style lang="scss" scoped>
    .sms-broadcast-text-box {
        display: flex;
        flex-direction: column;
        margin-bottom: $spacing-200;
    }

    .text-box-container {
        border: 1px solid $color-gray-400;
        border-radius: $border-radius * 0.5;
        font-size: $font-size-md;
        height: 100%;

        &:focus-within {
            border-color: $color-blue;
        }
        &.error {
            border-color: $color-red;
        }
    }

    .hide-border {
        border: none;
    }

    .text-box {
        position: relative;
        padding: $spacing-125 $spacing-200;
        outline: none;
        min-height: px-to-rem(60);
        height: 100%;

        &.placeholder:before {
            content: attr(placeholder);
            position: absolute;
            cursor: text;
            color: $color-text-disabled;
        }
    }

    .text {
        white-space: pre-wrap;
        overflow-wrap: break-word;
    }

    .overflow {
        background-color: $color-red-050;
    }

    .message {
        width: 100%;
        border: none;
        outline: none;
        font-size: $font-size-md;
        resize: none;
        border-radius: $spacing-100;
        padding: $spacing-125 $spacing-200;
    }
</style>
